import { useState } from "react";
import { startCorpusFeedRun, updateCorpusFeed } from "../../../../../../lib/corpusApi";
import { describeCorpusFeedRun } from "../../../../../../lib/corpusFeedView";
import { useAsyncHandler, type AsyncHandlerResult } from "../../../../../../hooks/useAsyncHandler";
import {
  useConfirmHandler,
  type UseConfirmHandlerResult,
} from "../../../../../../hooks/useConfirmHandler";
import type { CorpusFeed } from "@shared/types/corpus/api.ts";

export interface UseFeedActionsResult {
  /** Starts a run now. */
  runNow: AsyncHandlerResult;
  /** Pauses an active feed, or resumes a paused one. */
  togglePause: AsyncHandlerResult;
  /** Retires the feed once the person has confirmed: `confirm` asks, `dialogProps` is the dialog. */
  retire: {
    confirm: () => void;
    dialogProps: UseConfirmHandlerResult["dialogProps"];
  };
  /** What the last run request did, in words; null once it has been dismissed. */
  notice: string | null;
  dismissNotice: () => void;
}

/**
 * What an admin can do to a corpus feed: run it now, pause or resume it, or retire it. Each action calls `onChanged` afterwards, so the page shows the result.
 */
export function useFeedActions(
  feed: CorpusFeed,
  onChanged: () => Promise<void>
): UseFeedActionsResult {
  const [notice, setNotice] = useState<string | null>(null);

  const runNow = useAsyncHandler(async () => {
    setNotice(describeCorpusFeedRun(await startCorpusFeedRun(feed.id)));
    await onChanged();
  });

  const togglePause = useAsyncHandler(async () => {
    await updateCorpusFeed(feed.id, { state: feed.state === "active" ? "paused" : "active" });
    await onChanged();
  });

  const retireConfirmation = useConfirmHandler({
    title: "Retire this feed?",
    message:
      "A retired feed never runs again and cannot be resumed. A run in progress is cancelled. Papers already in the corpus stay.",
    confirmLabel: "Retire",
    confirmColor: "error",
  });

  const retire = {
    confirm: () =>
      retireConfirmation.handle(async () => {
        await updateCorpusFeed(feed.id, { state: "retired" });
        await onChanged();
      }),
    dialogProps: retireConfirmation.dialogProps,
  };

  return {
    runNow,
    togglePause,
    retire,
    notice,
    dismissNotice: () => setNotice(null),
  };
}
